// =============================================================================
// MeuDriver — Hook: useNotificationPrefsGranular (Batch T2)
// Controle granular de preferências de notificação in-app e email.
// Complementa useNotificationPreferences (Batch K2) com os novos campos.
// =============================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'
import { toast } from 'sonner'

// ─── Tipos ────────────────────────────────────────────────────────────────────

export interface GranularNotificationPrefs {
  // Agendamentos
  booking_confirmed_inapp:  boolean
  booking_confirmed_email:  boolean
  booking_cancelled_inapp:  boolean
  booking_cancelled_email:  boolean
  // Mensagens
  new_message_inapp:        boolean
  new_message_email:        boolean
  // Avaliações
  review_received_inapp:    boolean
  review_received_email:    boolean
  // Conquistas
  milestone_achieved_inapp: boolean
  badge_earned_inapp:       boolean
  // Plataforma
  announcement_inapp:       boolean
}

export type GranularNotificationPrefsUpdate = Partial<GranularNotificationPrefs>

// Defaults usados enquanto a linha ainda não existe no banco
const DEFAULTS: GranularNotificationPrefs = {
  booking_confirmed_inapp:  true,
  booking_confirmed_email:  true,
  booking_cancelled_inapp:  true,
  booking_cancelled_email:  true,
  new_message_inapp:        true,
  new_message_email:        true,
  review_received_inapp:    true,
  review_received_email:    true,
  milestone_achieved_inapp: true,
  badge_earned_inapp:       true,
  announcement_inapp:       true,
}

const GRANULAR_FIELDS = Object.keys(DEFAULTS) as Array<keyof GranularNotificationPrefs>

const QUERY_KEY = 'notification-prefs-granular'

// ─── useMyNotificationPrefs ───────────────────────────────────────────────────

export function useMyNotificationPrefs() {
  const { user } = useAuth()

  return useQuery<GranularNotificationPrefs>({
    queryKey: [QUERY_KEY, user?.id],
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 5,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('notification_preferences')
        .select(GRANULAR_FIELDS.join(', '))
        .eq('user_id', user!.id)
        .maybeSingle()

      if (error) throw new Error(error.message)
      if (!data) return { ...DEFAULTS }

      // Mescla com defaults para campos que possam ser null (legado)
      const result = { ...DEFAULTS }
      for (const key of GRANULAR_FIELDS) {
        const val = (data as unknown as Record<string, unknown>)[key]
        if (typeof val === 'boolean') result[key] = val
      }
      return result
    },
  })
}

// ─── useUpdateNotificationPrefs ───────────────────────────────────────────────

export function useUpdateNotificationPrefs() {
  const { user } = useAuth()
  const qc = useQueryClient()

  return useMutation<void, Error, GranularNotificationPrefsUpdate>({
    mutationFn: async (updates) => {
      if (!user?.id) throw new Error('Usuário não autenticado')

      const { error } = await supabase
        .from('notification_preferences')
        .upsert(
          { user_id: user.id, ...updates },
          { onConflict: 'user_id' },
        )

      if (error) throw new Error(error.message)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [QUERY_KEY, user?.id] })
      toast.success('Preferências salvas')
    },
    onError: (err) => {
      console.error('[useUpdateNotificationPrefs]', err.message)
      toast.error('Erro ao salvar preferências')
    },
  })
}
